import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SidebarNav } from "./Sidebar";
import { useAuth } from "@/context/AuthContext";

function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const submit = (e) => {
    e.preventDefault();
    navigate(`/suche?q=${encodeURIComponent(q.trim())}`);
  };
  return (
    <form role="search" onSubmit={submit} className="relative hidden max-w-xs flex-1 lg:block">
      <label htmlFor="global-search" className="sr-only">Globale Suche</label>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
      <input id="global-search" value={q} onChange={(e) => setQ(e.target.value)} data-testid="global-search-input"
        placeholder="Aktivitäten, Kreise, Inhalte, Organisationen suchen"
        className="h-10 w-full rounded-full border border-input bg-secondary/50 pl-9 pr-4 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-magenta" />
    </form>
  );
}

function LogoutConfirm({ open, setOpen, onConfirm, user, fallback }) {
  const rows = [["Konto", user?.name], ["Rolle", user?.role_label], ["Dieses Gerät", "Wird nach Bestätigung abgemeldet"],
    ["Andere Sitzungen", "Bleiben unverändert – verwaltbar unter «Mein Konto»"]];
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent data-testid="logout-confirm-dialog" className="w-[calc(100%-2rem)] rounded-xl"
        onCloseAutoFocus={(e) => {
          const el = fallback.current && document.querySelector(fallback.current);
          fallback.current = null;
          if (el) { e.preventDefault(); el.focus(); }
        }}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-2xl text-brand-purple">Abmeldung bestätigen</AlertDialogTitle>
          <AlertDialogDescription>
            Melde deine persönliche Control-Sitzung auf diesem Gerät ab. Nicht gespeicherte Eingaben gehen verloren.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <dl className="space-y-2" data-testid="logout-confirm-details">
          {rows.map(([k, v]) => (
            <div key={k} className="rounded-xl border border-[#E6E1F0] bg-white px-4 py-2.5">
              <dt className="text-xs font-semibold text-zinc-500">{k}</dt><dd className="mt-0.5 text-sm text-brand-purple">{v}</dd>
            </div>
          ))}
        </dl>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="logout-cancel">Angemeldet bleiben</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="logout-confirm" className="bg-brand-magenta hover:bg-brand-magenta-dark">Abmelden</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Avatar({ initials }) {
  return <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EEEAF7] text-xs font-bold text-brand-purple" aria-hidden="true">{initials}</span>;
}

// V3 mobile header: logo left, own account right; navigation and logout live in the bottom bar ("Mehr").
function MobileHeader({ initials }) {
  return (
    <>
      <Link to="/" className="lg:hidden" data-testid="mobile-logo" aria-label="Zur Übersicht">
        <img src="/visibilis-wordmark-dark.png" alt="Visibilis.life" className="h-8 w-auto" data-testid="mobile-wordmark" />
      </Link>
      <div className="flex-1 lg:hidden" />
      <Link to="/konto" className="lg:hidden" data-testid="account-link-mobile" aria-label="Mein Konto"><Avatar initials={initials} /></Link>
    </>
  );
}

export function TopBar({ mobileOpen, setMobileOpen }) {
  const { user, logout } = useAuth();
  const [confirm, setConfirm] = useState(false);
  const returnTo = useRef(null);
  const initials = (user?.name || "").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-white/95 backdrop-blur">
     <div className="flex h-16 min-w-0 items-center gap-3 px-4 sm:px-6">
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" closeLabel="Menü schließen" aria-describedby={undefined}
          className="w-[min(18rem,100vw)] max-w-[100vw] border-0 bg-brand-purple p-0 text-white sm:max-w-[min(18rem,100vw)]">
          <SheetTitle className="sr-only">Hauptnavigation</SheetTitle>
          <SidebarNav onNavigate={() => setMobileOpen(false)} onLogout={() => { returnTo.current = "[data-testid='mobile-nav-more']"; setMobileOpen(false); setConfirm(true); }} />
        </SheetContent>
      </Sheet>
      <MobileHeader initials={initials} />

      <span className="hidden shrink-0 text-lg font-bold text-brand-purple lg:inline" data-testid="topbar-title">Control OS</span>
      <GlobalSearch />
      <div className="hidden flex-1 lg:block" />
      <span className="hidden rounded-full bg-[#EEEAF7] px-3 py-1 text-xs font-bold text-brand-purple lg:inline-flex" data-testid="topbar-country-scope">
        {user?.countries?.length ? user.countries.join(", ") : "Alle Länder"}
      </span>
      <span className="hidden rounded-full bg-[#EEEAF7] px-3 py-1 text-xs font-bold text-brand-purple lg:inline-flex" data-testid="topbar-language">Deutsch</span>

      <Link to="/konto" data-testid="account-link"
        className="hidden min-w-0 max-w-xs items-center gap-2 rounded-full py-1 pl-1 pr-3 hover:bg-secondary lg:flex">
        <Avatar initials={initials} />
        <span className="min-w-0 break-words text-xs font-bold text-brand-purple" data-testid="topbar-user-name">{user?.name} · <span data-testid="topbar-user-role">{user?.role_label}</span></span>
      </Link>

      <Button variant="ghost" onClick={() => setConfirm(true)} data-testid="logout-button" aria-label="Abmelden"
        className="hidden h-10 shrink-0 gap-2 text-brand-purple hover:bg-brand-purple hover:text-white lg:inline-flex">
        <LogOut className="h-4 w-4" aria-hidden="true" />
        <span className="hidden xl:inline">Abmelden</span>
      </Button>
     </div>
      <LogoutConfirm open={confirm} setOpen={setConfirm} onConfirm={logout} user={user} fallback={returnTo} />
    </header>
  );
}
